import { ObjectId } from '@freightview/object-id'
import { omit } from 'lodash-es'

import type { UpsertLoadDTO, Workflow } from '@fv/models'

import type { ClientDTO } from '@/types'
import { buildStop } from '@/utils/locationFuncs'

import { buildHandlingUnitDTO } from '../commodities/load-items/loadItemHelpers'
import type {
  ItemStop,
  LoadHandlingUnitFormModel,
} from '../commodities/load-items/types'
import { buildLoadCustomsBrokerDTO } from '../customsBroker/brokerTypes'
import { loadTemplateToQuoteRequestFields } from '../load-templates/utils'
import { buildShipmentEquipmentDTO } from '../shipment-equipment-form/types'
import {
  buildLoadLocationDto,
  type ShipmentLocationFormModel,
} from '../shipment-location-form/types'
import type { ShipmentFormModel } from './types'

export const mapShipmentFormModelToLoadDTO = (
  model: ShipmentFormModel,
  workflow: Workflow,
): ClientDTO<UpsertLoadDTO> => {
  const items = model.isPickupOnly ? [] : model.items
  const destinationSequence = model.locations.length - 1
  return {
    items: items.map(item =>
      buildHandlingUnitDTO(workflow)({
        ...item,
        dropLocationSequence: item.dropLocationSequence ?? destinationSequence,
      }),
    ),
    locations: model.locations.map(buildLoadLocationDto),
    bolNumber: model.bolNum,
    ...(model.glCode !== undefined && { glCode: model.glCode }),
    equipment: buildShipmentEquipmentDTO(model.equipment, items, workflow),
    isLiveLoad: true,
    schedulePickup: model.schedulePickup,
    createBOL: !model.isPickupOnly,
    quoteRequestId: new ObjectId().toHexString(),
    workflow,
    ...omit(loadTemplateToQuoteRequestFields(model.selectedTemplate ?? null), [
      'glCode',
    ]),
    ...(!model.isPickupOnly &&
      model.customsBrokers && {
        customsBrokers: [buildLoadCustomsBrokerDTO(model.customsBrokers)],
      }),
    ...(!model.isPickupOnly &&
      model.emergencyContact && {
        emergencyContact: model.emergencyContact,
      }),
  }
}

export const getBookActionLabel = (workflow: Workflow) =>
  workflow === 'parcel'
    ? 'Create shipment & labels'
    : 'Schedule pickup & print BOL'

const resequenceLocations = (
  locations: ShipmentLocationFormModel[],
): ShipmentLocationFormModel[] =>
  locations.map((loc, i) => ({
    ...loc,
    id: loc.id ?? new ObjectId().toHexString(),
    sequence: i,
    type:
      i === 0 ? 'origin' : i === locations.length - 1 ? 'destination' : 'stop',
  }))

export const insertStopAfter = (
  locations: ShipmentLocationFormModel[],
  afterSequence: number,
) =>
  resequenceLocations([
    ...locations.slice(0, afterSequence + 1),
    buildStop({
      sequence: afterSequence + 1,
      shipType: 'business dock',
      stopType: 'delivery',
    }),
    ...locations.slice(afterSequence + 1),
  ])

export const removeStop = (
  locations: ShipmentLocationFormModel[],
  sequence: number,
) => resequenceLocations(locations.filter(l => l.sequence !== sequence))

const remapItemStops = (
  items: LoadHandlingUnitFormModel[],
  remap: (sequence: number) => number | undefined,
) =>
  items.map(item => ({
    ...item,
    ...(item.pickupLocationSequence !== undefined && {
      pickupLocationSequence: remap(item.pickupLocationSequence) ?? 0,
    }),
    ...(item.dropLocationSequence !== undefined && {
      dropLocationSequence: remap(item.dropLocationSequence),
    }),
  }))

export const shiftItemStopsForInsert = (
  items: LoadHandlingUnitFormModel[],
  afterSequence: number,
) => remapItemStops(items, s => (s > afterSequence ? s + 1 : s))

// Items dropped at a removed stop fall back to the destination.
export const shiftItemStopsForRemoval = (
  items: LoadHandlingUnitFormModel[],
  sequence: number,
) =>
  remapItemStops(items, s =>
    s === sequence ? undefined : s > sequence ? s - 1 : s,
  )

export const toItemStops = (
  locations: ShipmentLocationFormModel[],
): ItemStop[] =>
  locations.map(({ sequence, stopType, company, city, state }) => {
    const name =
      sequence === 0
        ? 'Origin'
        : sequence === locations.length - 1
          ? 'Destination'
          : `Stop ${sequence}`
    const place = company || [city, state].filter(Boolean).join(', ')
    return { sequence, stopType, label: place ? `${name} - ${place}` : name }
  })
